import type { FileUIPart } from "ai";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLocation, useSearch } from "wouter";
import {
  AlertCircle,
  Archive,
  Folder,
  Loader2,
  Menu,
  MoreHorizontal,
  Pencil,
  Pin,
  Plus,
  X,
} from "lucide-react";

import { KanbanBoard } from "@/components/kanban/KanbanBoard";
import { TaskFilterMenu } from "@/components/kanban/TaskFilterMenu";
import type { TaskDueFilter } from "@/lib/task-due-date";
import { KanbanSplitView } from "@/components/kanban/KanbanSplitView";
import { NotificationInbox } from "@/components/notification-inbox";
import { AgentDropdown } from "@/components/agent-dropdown";
import { ProjectEditDialog } from "@/components/project-edit-dialog";
import { ProjectShareSection, type ProjectShareSettings } from "@/components/project-share-section";
import { SettingsMenu } from "@/components/settings-menu";
import type { Agent } from "@/hooks/use-agents";
import { useAutoResize } from "@/hooks/use-auto-resize";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";
import type { UseUserPreferencesReturn } from "@/hooks/use-user-preferences";
import {
  type TaskAssigneeFilter,
  useKanbanTasks,
} from "@/hooks/use-kanban-tasks";
import { useMe } from "@/hooks/use-me";
import { useProjects, type UseProjectsReturn } from "@/hooks/use-projects";
import { useSharedProjects } from "@/hooks/projects-data-context";
import { useTags } from "@/hooks/use-tags";
import type { PushSubscriptionAPI } from "@/hooks/use-push-subscription";
import { useTasks } from "@/hooks/use-tasks";
import { useTaskHierarchyCapability } from "@/hooks/use-task-hierarchy-capability";
import { useTenantUsers } from "@/hooks/use-tenant-users";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { notifyProjectsChanged, notifyTaskChanged } from "@/lib/project-events";
import { shouldRetainSubtaskIdempotencyKey, subtaskCreateErrorKey } from "@/lib/subtask-creation";
import type {
  Project,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  Task,
  TaskStatus,
  TaskTitleSource,
  TaskUpdateRequest,
} from "@/lib/project-types";
import { cn } from "@/lib/utils";

const NEW_TASK_COMPOSER_QUERY_PARAM = "newTask";

type ActionError = "project-create" | "project-update" | "task-create" | "task-update" | "session-create" | null;

export interface ProjectTaskChatRenderProps {
  project: Project;
  task: Task;
  sessionId: string;
  /** Reserved for the normal ChatInterface API; board-created chats start empty. */
  initialMessage: string | null;
  /** Reserved for the normal ChatInterface API; board-created chats start empty. */
  initialFiles: FileUIPart[] | null;
  onInitialMessageSent: () => void;
  /** Close the task chat and return to the full board. */
  onClose: () => void;
  /** Task status shown in the task chat header; undefined for non-task chats. */
  taskStatus?: TaskStatus;
  /** Persist a task-status selection from the task chat header. */
  onTaskStatusChange?: (status: TaskStatus) => Promise<boolean> | boolean;
  onSaveDescription?: (description: string) => Promise<boolean>;
  onSaveTitle?: (title: string) => Promise<boolean>;
  onSaveDueDate?: (dueDate: string | null) => Promise<boolean>;
  onSaveAssignee?: (assigneeId: string | null) => Promise<boolean>;
  onSaveTags?: (tagIds: string[]) => Promise<boolean>;
  onArtifactRemoved?: (task: Task) => void;
}

export interface ProjectsWorkspaceProps {
  /** The project id from the route params; null when the route is not a project workspace. */
  projectId: string | null;
  getToken: () => Promise<string>;
  /** Incremented by the parent shell to request the create-project dialog. */
  createProjectSignal: number;
  /**
   * Creates a session for a legacy task fallback, or a direct project
   * conversation when projectId is supplied.
   */
  createProjectSession: (
    title: string,
    agentId: string,
    projectId?: string,
  ) => Promise<{ id: string } | null>;
  /** Best-effort cleanup for a legacy fallback session that definitely was not bound. */
  discardProjectSession?: (sessionId: string) => Promise<boolean>;
  renderTaskChat: (props: ProjectTaskChatRenderProps) => ReactNode;
  getTaskSessionAgentId?: (sessionId: string) => string | null;
  /** Canonical normal-chat agent data and tenant switch permission. */
  agents: Agent[];
  agentsLoading: boolean;
  agentsError: string | null;
  showAgentSwitcher: boolean;
  isAdminUser: boolean;
  /** Narrow shared-agent editor capability for an own-tenant administrator. */
  canManageTenantAgents?: boolean;
  user: { firstName?: string | null; email?: string | null };
  pushApi: PushSubscriptionAPI;
  preferencesApi: UseUserPreferencesReturn;
  userRole?: string;
  /** Opens a newly created task session in the normal conversation shell. */
  onOpenSession: (sessionId: string) => void;
  /** Identifies the open task chat for notification polling and view selection. */
  onActiveProjectSessionChange: (sessionId: string | null, agentId: string | null) => void;
  isMobile: boolean;
  isMenuOpen: boolean;
  onMenuToggle: () => void;
}

function actionErrorMessage(actionError: ActionError, t: (key: string) => string): string | null {
  switch (actionError) {
    case "project-create":
      return t("projects:projectCreateFailed");
    case "project-update":
      return t("projects:projectUpdateFailed");
    case "task-create":
      return t("projects:taskCreateFailed");
    case "task-update":
      return t("projects:taskUpdateFailed");
    case "session-create":
      return t("projects:projectSessionCreateFailed");
    default:
      return null;
  }
}

interface ProjectCreateDialogProps {
  open: boolean;
  projectName: string;
  projectDirective: string;
  projectAgent: string;
  isPrivate: boolean;
  agents: Agent[];
  agentsLoading: boolean;
  agentsError: string | null;
  creating: boolean;
  userRole?: string;
  getToken: () => Promise<string>;
  onOpenChange: (open: boolean) => void;
  onNameChange: (name: string) => void;
  onDirectiveChange: (directive: string) => void;
  onAgentChange: (agentId: string) => void;
  onShareSettingsChange: (settings: ProjectShareSettings) => void;
  onCreate: () => Promise<void>;
}

/**
 * The project creation surface asks for the fields the API needs to establish
 * a useful project: a name, an accessible default agent, and its privacy mode.
 */
function ProjectCreateDialog({
  open,
  projectName,
  projectDirective,
  projectAgent,
  isPrivate,
  agents,
  agentsLoading,
  agentsError,
  creating,
  userRole,
  getToken,
  onOpenChange,
  onNameChange,
  onDirectiveChange,
  onAgentChange,
  onShareSettingsChange,
  onCreate,
}: ProjectCreateDialogProps) {
  const { t } = useTranslation();
  // Older agno-server deployments omit allowed_roles entirely. Treat that the
  // same as an open agent so creating a project remains backwards compatible.
  const selectedAgentAllowedRoles = agents.find((agent) => agent.id === projectAgent)?.allowed_roles ?? null;
  const shareResetKey = open
    ? `create:${projectAgent}:${selectedAgentAllowedRoles?.join(",") ?? ""}`
    : "";
  const {
    ref: directiveResizeRef,
    resize: resizeDirective,
    resizeForValueChange: resizeDirectiveForValueChange,
  } = useAutoResize(10, 1);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(resizeDirective);
    return () => cancelAnimationFrame(frame);
  }, [open, resizeDirective]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("projects:sectionProjectsNew")}</DialogTitle>
          <DialogDescription>{t("projects:projectCreationDescription")}</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void onCreate();
          }}
        >
          <label className="grid gap-1.5 text-sm font-medium">
            {t("projects:projectName")}
            <Textarea
              autoFocus
              required
              value={projectName}
              onChange={(event) => onNameChange(event.target.value)}
              placeholder={t("projects:placeholders.namePlaceholder")}
              className="min-h-9 resize-none font-normal"
              rows={1}
            />
          </label>
          <label className="grid gap-1.5 text-sm font-medium">
            {t("projects:projectAgent")}
            <AgentDropdown
              agents={agents}
              currentAgentId={projectAgent}
              loading={agentsLoading}
              error={agentsError}
              selectionLocked={false}
              getToken={getToken}
              onSelect={onAgentChange}
              fullWidth
              isAdminUser={userRole === "admin"}
            />
            <span className="text-xs font-normal text-muted-foreground">
              {t("projects:projectAgentHint")}
            </span>
          </label>
          <label className="grid gap-1.5 text-sm font-medium">
            {t("projects:projectDirective")}
            <Textarea
              ref={directiveResizeRef}
              value={projectDirective}
              onChange={(event) => {
                onDirectiveChange(event.target.value);
                resizeDirectiveForValueChange();
              }}
              placeholder={t("projects:placeholders.directivePlaceholder")}
              maxLength={500}
              className="min-h-9 resize-none overflow-hidden font-normal"
              rows={1}
            />
          </label>
          <ProjectShareSection
            initialSettings={{ isPrivate, allowedRoles: selectedAgentAllowedRoles }}
            userRole={userRole}
            resetKey={shareResetKey}
            onSettingsChange={onShareSettingsChange}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit" disabled={creating || !projectName.trim() || !projectAgent || agentsLoading}>
              {creating && <Loader2 className="animate-spin" />}
              {t("projects:createProject")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Project content rendered inside ChatPage's authenticated session shell.
 * It owns project/task data and routes task prompts through the existing
 * session lifecycle provided by the shell.
 */
export function ProjectsWorkspace(props: ProjectsWorkspaceProps) {
  const sharedProjects = useSharedProjects();

  if (sharedProjects) {
    return <ProjectsWorkspaceContent {...props} projectsApi={sharedProjects} />;
  }
  return <StandaloneProjectsWorkspace {...props} />;
}

function StandaloneProjectsWorkspace(props: ProjectsWorkspaceProps) {
  const projectsApi = useProjects(props.getToken);
  return <ProjectsWorkspaceContent {...props} projectsApi={projectsApi} />;
}

function ProjectsWorkspaceContent({
  /** The project id from the route params; null when the route is not a project workspace. */
  projectId,

  getToken,
  createProjectSignal,
  createProjectSession,
  discardProjectSession,
  renderTaskChat,
  getTaskSessionAgentId,
  agents,
  agentsLoading,
  agentsError,
  showAgentSwitcher,
  isAdminUser,
  canManageTenantAgents = false,
  user,
  pushApi,
  preferencesApi,
  userRole,
  onOpenSession,
  onActiveProjectSessionChange,
  isMobile,
  isMenuOpen,
  onMenuToggle,
  projectsApi,
}: ProjectsWorkspaceProps & { projectsApi: UseProjectsReturn }) {
  const { t } = useTranslation();
  const { me } = useMe(getToken);
  const [location, navigate] = useLocation();
  const search = useSearch();
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const selectedProjectFromLink = projectId ?? null;
  const selectedTaskFromLink = query.get("task");
  const newProjectRequested = location === "/project/new";
  const newTaskComposerOpen = query.get(NEW_TASK_COMPOSER_QUERY_PARAM) === "1";
  const {
    projects,
    loading: projectsLoading,
    listError: projectsListError,
    create: createProject,
    get: getProject,
    update: updateProject,
    archive,
    pin,
    unpin,
    upsert: upsertProject,
    refresh: refreshProjects,
  } = projectsApi;
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const hierarchyCapability = useTaskHierarchyCapability(activeProjectId, getToken);
  const [assigneeFilter, setAssigneeFilter] = useState<TaskAssigneeFilter>(
    () => (localStorage.getItem("kanban-assignee-filter") as TaskAssigneeFilter) || "all",
  );
  const [dueFilter, setDueFilter] = useState<TaskDueFilter>("all");
  const [tagIds, setTagIds] = useState<string[]>([]);
  const {
    create: createTask,
    createSubtask,
    update: updateTask,
    bindSessionDetailed,
    createSessionIfUnbound,
  } = useTasks(getToken);
  const {
    tags: projectTags,
    create: createTag,
    archive: archiveTag,
    restore: restoreTag,
  } = useTags(getToken, activeProjectId);
  const kanbanTasks = useKanbanTasks(
    getToken,
    activeProjectId,
    assigneeFilter,
    undefined,
    dueFilter,
    preferencesApi.data?.preferences.timezone,
    tagIds,
    hierarchyCapability.state === "supported",
  );
  const { tenantUsers, refresh: refreshTenantUsers } = useTenantUsers(getToken);

  const [projectName, setProjectName] = useState("");
  const [projectDirective, setProjectDirective] = useState("");
  const [projectAgent, setProjectAgent] = useState("");
  const [projectPrivate, setProjectPrivate] = useState(false);
  const [projectAllowedRoles, setProjectAllowedRoles] = useState<string[] | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editProjectId, setEditProjectId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<ActionError>(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [hydratingProjectId, setHydratingProjectId] = useState<string | null>(null);
  const [unavailableProjectId, setUnavailableProjectId] = useState<string | null>(null);
  // The task hook receives the bound task asynchronously. Hold the confirmed
  // DTO long enough to render its chat while that state update propagates.
  const [optimisticChatTask, setOptimisticChatTask] = useState<Task | null>(null);
  const [scrollToMovedTaskId, setScrollToMovedTaskId] = useState<string | null>(null);
  const projectScrollRef = useRef<HTMLDivElement>(null);
  const backendReconnectKey = useBackendReconnectKey();
  // React state is asynchronous, while rapid pointer events are synchronous.
  // This mirrors creatingTask so different unbound cards cannot start more
  // than one session creation before the component rerenders.
  const creatingTaskRef = useRef(false);
  const pendingTaskClickRef = useRef<Set<string>>(new Set());
  const pendingSubtaskCreateRef = useRef<Set<string>>(new Set());
  const subtaskIdempotencyKeysRef = useRef<Map<string, string>>(new Map());
  const deepLinkLookupRef = useRef<string | null>(null);
  const projectHydrationRef = useRef<string | null>(null);
  const newProjectRouteRef = useRef<string | null>(null);

  useEffect(() => {
    localStorage.setItem("kanban-assignee-filter", assigneeFilter);
  }, [assigneeFilter]);
  useEffect(() => {
    if (kanbanTasks.dueDateSupported === false) setDueFilter("all");
  }, [kanbanTasks.dueDateSupported]);
  useEffect(() => {
    if (kanbanTasks.tagFilterSupported === false) setTagIds([]);
  }, [kanbanTasks.tagFilterSupported]);
  useEffect(() => setTagIds([]), [activeProjectId]);

  useEffect(() => {
    if (createProjectSignal > 0) {
      setCreateDialogOpen(true);
    }
  }, [createProjectSignal]);

  useEffect(() => {
    if (!newProjectRequested) {
      newProjectRouteRef.current = null;
      return;
    }
    // Keep a dismissed /project/new dialog closed until the URL changes, while a
    // fresh deep link or reload still opens the existing creation dialog.
    if (newProjectRouteRef.current === location) return;
    newProjectRouteRef.current = location;
    setCreateDialogOpen(true);
  }, [newProjectRequested, location]);

  const visibleProjects = projects;
  const availableProjectAgents = useMemo(() => {
    const accessibleAgents = agents
      .filter((agent) => agent.type === "agent")
      .sort((a, b) => {
        const orderDifference = (a.order ?? 100) - (b.order ?? 100);
        return orderDifference !== 0 ? orderDifference : a.name.localeCompare(b.name);
      });
    return accessibleAgents;
  }, [agents]);

  useEffect(() => {
    setActiveProjectId((current) => {
      if (selectedProjectFromLink) {
        // A catalog route may target a project that is accessible but not in
        // the pinned sidebar list. Leave selection empty until its detail
        // response is hydrated below instead of opening a different project.
        return visibleProjects.some((project) => project.id === selectedProjectFromLink)
          ? selectedProjectFromLink
          : null;
      }
      if (current && visibleProjects.some((project) => project.id === current)) return current;
      return visibleProjects[0]?.id ?? null;
    });
  }, [selectedProjectFromLink, visibleProjects]);

  // Unpinned projects are loaded through the detail endpoint. Reset its
  // one-shot guard after connectivity returns so deep links recover too.
  useEffect(() => {
    projectHydrationRef.current = null;
    setUnavailableProjectId(null);
  }, [backendReconnectKey]);

  useEffect(() => {
    if (!selectedProjectFromLink) {
      projectHydrationRef.current = null;
      setHydratingProjectId(null);
      setUnavailableProjectId(null);
      return;
    }

    const selectedProjectIsLoaded = visibleProjects.some(
      (project) => project.id === selectedProjectFromLink,
    );
    if (selectedProjectIsLoaded) {
      projectHydrationRef.current = null;
      setHydratingProjectId(null);
      setUnavailableProjectId(null);
      return;
    }

    if (projectsLoading) {
      // Invalidate an older route's request while the pinned list is still
      // resolving, then defer the detail fetch until it has settled.
      projectHydrationRef.current = null;
      setHydratingProjectId(selectedProjectFromLink);
      setUnavailableProjectId(null);
      return;
    }

    if (projectHydrationRef.current === selectedProjectFromLink) return;
    projectHydrationRef.current = selectedProjectFromLink;
    setHydratingProjectId(selectedProjectFromLink);
    setUnavailableProjectId(null);

    void (async () => {
      const project = await getProject(selectedProjectFromLink);
      if (projectHydrationRef.current !== selectedProjectFromLink) return;

      if (project) {
        upsertProject(project);
        setUnavailableProjectId(null);
      } else {
        // The detail endpoint deliberately returns the same 404 for a deleted
        // or no-longer-visible project. Keep the UI equally non-enumerating.
        setUnavailableProjectId(selectedProjectFromLink);
      }
      setHydratingProjectId(null);
    })();
  }, [
    backendReconnectKey,
    getProject,
    projectsLoading,
    selectedProjectFromLink,
    upsertProject,
    visibleProjects,
  ]);

  useEffect(() => {
    setProjectAgent((current) => {
      if (current && availableProjectAgents.some((agent) => agent.id === current)) return current;
      return availableProjectAgents[0]?.id ?? "";
    });
  }, [availableProjectAgents]);

  const activeProject = useMemo(
    () => visibleProjects.find((project) => project.id === activeProjectId) ?? null,
    [activeProjectId, visibleProjects],
  );
  const isSelectedProjectUnavailable = Boolean(
    selectedProjectFromLink && unavailableProjectId === selectedProjectFromLink,
  );
  const isSelectedProjectHydrating = Boolean(
    selectedProjectFromLink
    && !activeProject
    && !isSelectedProjectUnavailable
    && hydratingProjectId === selectedProjectFromLink,
  );
  const activeProjectAgentName = useMemo(() => {
    if (!activeProject) return undefined;
    return availableProjectAgents.find((agent) => agent.id === activeProject.default_agent_id)?.name
      ?? activeProject.default_agent_id;
  }, [activeProject, availableProjectAgents]);
  const agentNameForTask = useCallback((task: Task) => {
    const agentId = task.session_id ? getTaskSessionAgentId?.(task.session_id) : null;
    if (!agentId) return undefined;
    return availableProjectAgents.find((agent) => agent.id === agentId)?.name ?? agentId;
  }, [getTaskSessionAgentId, availableProjectAgents]);
  const agentIdForTask = useCallback((task: Task) => task.session_id
    ? getTaskSessionAgentId?.(task.session_id) ?? undefined
    : undefined, [getTaskSessionAgentId]);
  const activeTasks = activeProjectId ? kanbanTasks.tasks : [];
  const kanbanColumns = kanbanTasks.columns;
  const selectedTask = useMemo(
    () => activeTasks.find((task) => task.id === selectedTaskFromLink) ?? null,
    [activeTasks, selectedTaskFromLink],
  );
  const hierarchyDetailLookupRef = useRef<string | null>(null);
  useEffect(() => {
    if (hierarchyCapability.state !== "supported" || !selectedTask || Object.prototype.hasOwnProperty.call(selectedTask, "parent_task_id")) {
      if (!selectedTask) hierarchyDetailLookupRef.current = null;
      return;
    }
    const key = `${selectedTask.project_id}:${selectedTask.id}`;
    if (hierarchyDetailLookupRef.current === key) return;
    hierarchyDetailLookupRef.current = key;
    void kanbanTasks.fetchTask(selectedTask.id);
  }, [hierarchyCapability.state, kanbanTasks.fetchTask, selectedTask]);
  const chatTask = selectedTask?.session_id
    ? selectedTask
    : optimisticChatTask?.id === selectedTaskFromLink
      && optimisticChatTask.project_id === activeProjectId
      && optimisticChatTask.session_id
      ? optimisticChatTask
      : null;
  const taskViewAgentId = (chatTask?.session_id && getTaskSessionAgentId?.(chatTask.session_id)) || activeProject?.default_agent_id || "";
  const isTaskChatOpen = Boolean(chatTask?.session_id);
  const needsDeepLinkLookup = Boolean(
    selectedTaskFromLink
    && activeProject
    && kanbanTasks.initialized
    && !selectedTask
    && !kanbanTasks.taskLookupError,
  );
  const isTaskSelectionPending = Boolean(
    selectedTaskFromLink &&
    activeProject &&
    (
      !kanbanTasks.initialized
      || kanbanTasks.taskLookupLoading
      || needsDeepLinkLookup
    ),
  );
  const hasMissingSelectedTask = Boolean(selectedTaskFromLink && !selectedTask && !isTaskSelectionPending);
  const editProject = useMemo(
    () => (editProjectId
      ? visibleProjects.find((project) => project.id === editProjectId) ?? null
      : null),
    [editProjectId, visibleProjects],
  );

  useEffect(() => {
    if (
      !selectedTaskFromLink
      || !activeProject
      || !kanbanTasks.initialized
      || selectedTask
    ) {
      if (!selectedTaskFromLink || selectedTask) deepLinkLookupRef.current = null;
      return;
    }

    const lookupKey = `${activeProject.id}:${selectedTaskFromLink}`;
    if (deepLinkLookupRef.current === lookupKey) return;
    deepLinkLookupRef.current = lookupKey;
    void kanbanTasks.fetchTask(selectedTaskFromLink);
  }, [
    activeProject,
    kanbanTasks.fetchTask,
    kanbanTasks.initialized,
    selectedTask,
    selectedTaskFromLink,
  ]);

  useEffect(() => {
    setOptimisticChatTask((current) => {
      if (!current) return null;
      // Do not discard the optimistic handoff in an intermediate commit before
      // the URL selection is visible. Task-session binding, the route mutation, and
      // the task-list state update can all settle in separate commits.
      if (selectedTask?.id === current.id && selectedTask.session_id) return null;
      if (selectedTaskFromLink && current.id !== selectedTaskFromLink) return null;
      if (activeProjectId && current.project_id !== activeProjectId) return null;
      return current;
    });
  }, [activeProjectId, selectedTask, selectedTaskFromLink]);

  useEffect(() => {
    onActiveProjectSessionChange(chatTask?.session_id ?? null, chatTask ? taskViewAgentId : null);
    return () => onActiveProjectSessionChange(null, null);
  }, [chatTask?.session_id, taskViewAgentId, onActiveProjectSessionChange]);

  const openProject = useCallback((projectId: string, taskId?: string) => {
    setScrollToMovedTaskId(null);
    const params = new URLSearchParams();
    if (taskId) {
      params.set("task", taskId);
    } else {
      setOptimisticChatTask(null);
    }
    params.delete("new");
    params.delete("newTask");
    const queryString = params.toString();
    navigate(`/project/${encodeURIComponent(projectId)}${queryString ? `?${queryString}` : ""}`);
  }, [navigate]);

  const consumeNewTaskComposerSignal = useCallback(() => {
    if (!selectedProjectFromLink || !newTaskComposerOpen) return;
    const params = new URLSearchParams(search);
    params.delete(NEW_TASK_COMPOSER_QUERY_PARAM);
    const queryString = params.toString();
    navigate(
      `/project/${encodeURIComponent(selectedProjectFromLink)}${queryString ? `?${queryString}` : ""}`,
    );
  }, [navigate, newTaskComposerOpen, search, selectedProjectFromLink]);

  const openProjectCreation = useCallback(() => {
    setCreateDialogOpen(true);
  }, []);

  const closeProjectCreation = useCallback(() => {
    setCreateDialogOpen(false);
  }, []);

  const resetProjectForm = useCallback(() => {
    const defaultAgent = availableProjectAgents[0];
    setProjectName("");
    setProjectDirective("");
    setProjectAgent(defaultAgent?.id ?? "");
    setProjectPrivate(false);
    setProjectAllowedRoles(defaultAgent?.allowed_roles ?? null);
  }, [availableProjectAgents]);

  const handleCreateProject = useCallback(async () => {
    const name = projectName.trim();
    const defaultAgentId = projectAgent;
    if (!name || creatingProject || !availableProjectAgents.some((agent) => agent.id === defaultAgentId)) return;

    setActionError(null);
    setCreatingProject(true);
    const input: ProjectCreateRequest = {
      name,
      directive: projectDirective.trim() || undefined,
      default_agent_id: defaultAgentId,
      is_private: projectPrivate,
      allowed_roles: projectAllowedRoles,
    };

    try {
      const project = await createProject(input);
      if (!project) {
        setActionError("project-create");
        return;
      }
      setActiveProjectId(project.id);
      notifyProjectsChanged({ source: "api", type: "upsert", project });
      resetProjectForm();
      openProject(project.id);
      setCreateDialogOpen(false);
    } finally {
      setCreatingProject(false);
    }
  }, [
    availableProjectAgents,
    creatingProject,
    createProject,
    openProject,
    projectAgent,
    projectDirective,
    projectName,
    projectPrivate,
    projectAllowedRoles,
    resetProjectForm,
  ]);

  const handleSaveProject = useCallback(async (
    projectId: string,
    input: ProjectUpdateRequest,
  ): Promise<boolean> => {
    setActionError(null);
    const project = visibleProjects.find((item) => item.id === projectId);
    if (!project) return false;

    const updatedProject = await updateProject(projectId, input);
    if (!updatedProject) {
      setActionError("project-update");
      return false;
    }
    notifyProjectsChanged({ source: "api", type: "upsert", project: updatedProject });
    return true;
  }, [updateProject, visibleProjects]);

  const handleArchiveProject = useCallback(async (projectId: string) => {
    if (!window.confirm(t("projects:archiveProjectConfirm"))) return;
    setActionError(null);
    const moveAwayFromArchivedProject = () => {
      if (activeProjectId !== projectId) return;
      const nextProjectId = visibleProjects.find((project) => project.id !== projectId)?.id;
      setActiveProjectId(nextProjectId ?? null);
      if (nextProjectId) {
        openProject(nextProjectId);
        return;
      }
      const params = new URLSearchParams(search);
      params.delete("project");
      params.delete("task");
      const nextSearch = params.toString();
      navigate(nextSearch ? `/projects/list?${nextSearch}` : "/projects/list");
    };

    if (!(await archive(projectId))) {
      setActionError("project-update");
      return;
    }
    notifyProjectsChanged({ source: "api", type: "remove", projectId });
    moveAwayFromArchivedProject();
  }, [activeProjectId, archive, navigate, openProject, search, t, visibleProjects]);

  const handlePinProject = useCallback(async (project: Project) => {
    setActionError(null);
    const nextPinned = !project.is_pinned;

    const updated = nextPinned ? await pin(project.id) : await unpin(project.id);
    if (!updated) {
      setActionError("project-update");
      return;
    }
    notifyProjectsChanged({
      source: "api",
      type: "upsert",
      project: { ...project, is_pinned: nextPinned },
    });
  }, [pin, unpin]);

  const handleTaskStatusChange = useCallback(async (
    taskId: string,
    status: TaskStatus,
  ): Promise<boolean> => {
    const task = activeTasks.find((item) => item.id === taskId);
    if (!task || task.status === status) return true;
    setActionError(null);

    const moved = await kanbanTasks.moveTask(task.id, status, async () => {
      const updated = await updateTask(task.id, { status });
      if (!updated) setActionError("task-update");
      return updated;
    });
    if (!moved) {
      setActionError("task-update");
      return false;
    }
    if (isTaskChatOpen && selectedTaskFromLink === task.id) {
      setScrollToMovedTaskId(task.id);
    }
    return true;
  }, [activeTasks, isTaskChatOpen, kanbanTasks.moveTask, selectedTaskFromLink, updateTask]);

  const handleUpdateTask = useCallback(async (
    taskId: string,
    updates: TaskUpdateRequest,
  ): Promise<boolean> => {
    const task = activeTasks.find((item) => item.id === taskId);
    if (!task) return false;

    const input: TaskUpdateRequest = {};
    if (updates.title !== undefined) {
      const title = updates.title.trim();
      if (!title) return false;
      if (title !== task.title) input.title = title;
    }
    if (updates.description !== undefined && updates.description !== task.description) {
      // Preserve null as an intentional clear and leave Markdown whitespace
      // untouched; server-side validation owns the size limit.
      input.description = updates.description;
    }
    if (updates.assigned_to !== undefined && updates.assigned_to !== task.assigned_to) {
      input.assigned_to = updates.assigned_to;
    }
    if (updates.due_date !== undefined && updates.due_date !== task.due_date) {
      input.due_date = updates.due_date;
    }
    if (updates.tags !== undefined) {
      const tagIds = [...new Set(updates.tags)];
      const currentTagIds = (task.tags ?? []).map((tag) => tag.id);
      if (
        tagIds.length !== currentTagIds.length
        || tagIds.some((tagId, index) => tagId !== currentTagIds[index])
      ) {
        input.tags = tagIds;
      }
    }
    if (Object.keys(input).length === 0) return true;

    setActionError(null);
    const updated = await updateTask(task.id, input);
    if (!updated) {
      setActionError("task-update");
      return false;
    }
    kanbanTasks.upsert(updated);
    return true;
  }, [activeTasks, kanbanTasks.upsert, updateTask]);

  const handleUpdateTags = useCallback(async (
    taskId: string,
    tagIds: string[],
  ): Promise<boolean> => handleUpdateTask(taskId, { tags: tagIds }), [handleUpdateTask]);

  const handleCreateTag = useCallback(async (
    name: string,
    color?: string,
  ) => {
    const tag = await createTag(name, color);
    if (!tag) setActionError("task-update");
    return tag;
  }, [createTag]);

  const handleArchiveTag = useCallback(async (tagId: string): Promise<boolean> => {
    const archived = await archiveTag(tagId);
    if (!archived) setActionError("task-update");
    return archived;
  }, [archiveTag]);

  const handleRestoreTag = useCallback(async (tagId: string): Promise<boolean> => {
    const restored = await restoreTag(tagId);
    if (!restored) setActionError("task-update");
    return restored;
  }, [restoreTag]);

  const handleCreateKanbanTask = useCallback(async (
    title: string,
    status: TaskStatus,
    assignedTo?: string | null,
    tagIds?: string[],
    dueDate?: string | null,
    titleSource?: TaskTitleSource,
  ): Promise<Task | null> => {
    if (!activeProject || creatingTask || creatingTaskRef.current) return null;
    creatingTaskRef.current = true;
    setActionError(null);
    setCreatingTask(true);

    try {
      // Ask current servers to create the card in its requested lane. Older
      // servers may ignore the optional field, so retain the PATCH fallback.
      // The backend atomically creates and binds the task's session.
      let task = await createTask(
        activeProject.id,
        title,
        assignedTo,
        tagIds,
        undefined,
        titleSource,
        status,
        dueDate,
      );
      if (!task) {
        setActionError("task-create");
        return null;
      }
      kanbanTasks.upsert(task);
      if (task.status !== status) {
        let persistedTask: Task | null = null;
        const moved = await kanbanTasks.moveTask(task.id, status, async () => {
          persistedTask = await updateTask(task!.id, { status });
          return persistedTask;
        });
        if (!moved || !persistedTask) {
          setActionError("task-update");
          return null;
        }
        task = persistedTask;
      }

      return task;
    } finally {
      creatingTaskRef.current = false;
      setCreatingTask(false);
    }
  }, [
    activeProject,
    createTask,
    creatingTask,
    kanbanTasks.moveTask,
    kanbanTasks.upsert,
    updateTask,
  ]);

  const bindLegacyTaskSession = useCallback(async (
    task: Task,
    project: Project,
  ): Promise<Task | null> => {
    // This is only reached after an older server rejects the atomic
    // create-if-unbound extension. Pass project_id on current servers; an
    // older project_id-less retry is immediately synchronized by bindSession.
    const session = await createProjectSession(
      task.title,
      project.default_agent_id,
      project.id,
    );
    if (!session) {
      setActionError("session-create");
      return null;
    }

    const binding = await bindSessionDetailed(task.id, session.id);
    if (binding.task) return binding.task;

    // A 4xx is a definitive non-commit from the bind API, so the just-created
    // caller-owned session cannot have become a task conversation. Clean it
    // up rather than leaving a stranded sidebar entry. Transport/5xx failures
    // stay untouched because the server outcome is ambiguous in those cases.
    if (binding.status !== null && binding.status >= 400 && binding.status < 500) {
      await discardProjectSession?.(session.id);
    }
    setActionError("task-update");
    return null;
  }, [bindSessionDetailed, createProjectSession, discardProjectSession]);

  const handleKanbanCardClick = useCallback(async (task: Task) => {
    if (!activeProject) return false;
    if (task.session_id) {
      openProject(activeProject.id, task.id);
      return true;
    }

    // Agent-created cards can intentionally remain unbound until a project
    // member opens them; older task rows use the same recovery path.
    if (creatingTask || creatingTaskRef.current || pendingTaskClickRef.current.has(task.id)) return false;

    creatingTaskRef.current = true;
    pendingTaskClickRef.current.add(task.id);
    setActionError(null);
    setCreatingTask(true);
    try {
      const started = await createSessionIfUnbound(task.id);
      let boundTask = started.task;
      if (!boundTask && started.needsLegacyFallback) {
        boundTask = await bindLegacyTaskSession(task, activeProject);
      }
      if (!boundTask) {
        setActionError("task-update");
        return false;
      }
      kanbanTasks.upsert(boundTask);
      setOptimisticChatTask(boundTask);
      openProject(activeProject.id, boundTask.id);
      return true;
    } finally {
      pendingTaskClickRef.current.delete(task.id);
      creatingTaskRef.current = false;
      setCreatingTask(false);
    }
  }, [
    activeProject,
    bindLegacyTaskSession,
    createSessionIfUnbound,
    creatingTask,
    kanbanTasks.upsert,
    openProject,
  ]);

  const handleAddSubtask = useCallback(async (task: Task) => {
    if (hierarchyCapability.state !== "supported") {
      toast.error(t("projects:subtasks.serverUnsupported"));
      return;
    }
    if (task.status === "cancelled") {
      toast.error(t("projects:subtasks.ancestorInactive"));
      return;
    }
    if (pendingSubtaskCreateRef.current.has(task.id)) return;

    pendingSubtaskCreateRef.current.add(task.id);
    const key = subtaskIdempotencyKeysRef.current.get(task.id) ?? crypto.randomUUID();
    subtaskIdempotencyKeysRef.current.set(task.id, key);
    try {
      const result = await createSubtask(
        task.project_id,
        task.id,
        t("projects:subtasks.new"),
        key,
        "default",
      );
      if (!result.task?.session_id) {
        if (!shouldRetainSubtaskIdempotencyKey(result.status)) subtaskIdempotencyKeysRef.current.delete(task.id);
        toast.error(t(`projects:${subtaskCreateErrorKey(result.status)}`));
        return;
      }
      subtaskIdempotencyKeysRef.current.delete(task.id);
      notifyTaskChanged(result.task);
      kanbanTasks.upsert(result.task);
      setOptimisticChatTask(result.task);
      openProject(result.task.project_id, result.task.id);
    } finally {
      pendingSubtaskCreateRef.current.delete(task.id);
    }
  }, [createSubtask, hierarchyCapability.state, kanbanTasks.upsert, openProject, t]);

  const handleCreateProjectConversation = useCallback(async () => {
    if (!activeProject) return;

    setActionError(null);
    const session = await createProjectSession(
      t("projects:newConversation"),
      activeProject.default_agent_id,
      activeProject.id,
    );
    if (!session) {
      setActionError("session-create");
      return;
    }

    onOpenSession(session.id);
  }, [
    activeProject,
    createProjectSession,
    onOpenSession,
    t,
  ]);

  const handleKanbanDeleteTask = useCallback((taskId: string) => {
    void handleTaskStatusChange(taskId, "cancelled");
  }, [handleTaskStatusChange]);

  const formError = actionErrorMessage(actionError, t);
  const projectLoadError = projectsListError ? t("projects:projectLoadFailed") : null;
  const kanbanBoard = activeProject ? (
    <KanbanBoard
      key={activeProject.id}
      columns={kanbanColumns}
      onStatusChange={handleTaskStatusChange}
      onBulkAction={(request) => kanbanTasks.bulkAction(request, projectTags)}
      onCardClick={(task) => { void handleKanbanCardClick(task); }}
      onDeleteTask={handleKanbanDeleteTask}
      onAddSubtask={(task) => { void handleAddSubtask(task); }}
      hierarchySupported={hierarchyCapability.state === "supported"}
      onCreateTask={handleCreateKanbanTask}
      onUpdateTask={handleUpdateTask}
      allTags={projectTags}
      onUpdateTags={handleUpdateTags}
      onCreateTag={handleCreateTag}
      onArchiveTag={handleArchiveTag}
      onRestoreTag={handleRestoreTag}
      onLoadMore={kanbanTasks.loadMore}
      onRetryColumn={kanbanTasks.retry}
      onRevealPending={kanbanTasks.revealPending}
      onColumnAtTopChange={kanbanTasks.setColumnAtTop}
      scrollContainerRef={projectScrollRef}
      tenantUsers={tenantUsers}
      defaultAssigneeId={me?.user_id}
      dueDateSupported={kanbanTasks.dueDateSupported === true}
      timezone={preferencesApi.data?.preferences.timezone}
      agentName={activeProjectAgentName}
      agentNameForTask={agentNameForTask}
      agentIdForTask={agentIdForTask}
      defaultAgentId={activeProject.default_agent_id}
      activeTaskId={selectedTaskFromLink}
      scrollToTaskId={isTaskChatOpen && scrollToMovedTaskId === selectedTaskFromLink ? scrollToMovedTaskId : null}
      restoreFocusOnDrop={isTaskChatOpen}
      shouldOpenCreateForm={newTaskComposerOpen}
      onCreateFormOpened={consumeNewTaskComposerSignal}
      compact={isTaskChatOpen}
    />
  ) : null;

  return (
    <main className="flex min-h-0 min-w-0 flex-1 flex-col bg-background text-foreground">
      <div
        ref={projectScrollRef}
        data-testid="projects-page-scroll-region"
        className={cn(
          "min-h-0 min-w-0 flex-1 overflow-y-auto",
          activeProject && "flex flex-col",
        )}
      >
        <div className="flex min-h-full min-w-0 flex-col">
          <header className="flex h-[53px] min-w-0 shrink-0 items-center gap-2 border-b px-3 py-2.5">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {isMobile && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0"
                  onClick={onMenuToggle}
                  aria-label={isMenuOpen ? t("header.closeMenu") : t("header.openMenu")}
                >
                  {isMenuOpen ? <X /> : <Menu />}
                </Button>
              )}
              <Folder className="size-4 shrink-0 text-primary" />
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <h1 className="min-w-0 flex-1 truncate text-sm font-semibold" title={activeProject?.directive || undefined}>
                  {activeProject?.name ?? t("projects:sectionProjects")}
                </h1>
                {activeProject && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-7 shrink-0"
                        aria-label={t("projects:projectActions")}
                      >
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => { void handleCreateProjectConversation(); }}>
                        <Plus className="size-4" />
                        {t("projects:newConversation")}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => {
                          setEditProjectId(activeProject.id);
                          setEditDialogOpen(true);
                        }}
                      >
                        <Pencil className="size-4" />
                        {t("projects:editProject")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => { void handlePinProject(activeProject); }}>
                        <Pin className={cn("size-4 stroke-[1.5] fill-none", activeProject.is_pinned && "text-muted-foreground/60")} />
                        {activeProject.is_pinned ? t("projects:unpinProject") : t("projects:pinProject")}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onSelect={() => { void handleArchiveProject(activeProject.id); }}
                      >
                        <Archive className="size-4" />
                        {t("projects:archiveProject")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            {activeProject && <TaskFilterMenu assignee={assigneeFilter} onAssigneeChange={setAssigneeFilter} due={dueFilter} onDueChange={setDueFilter}
              dueSupported={kanbanTasks.dueDateSupported} tagIds={tagIds} onTagIdsChange={setTagIds}
              tagSupported={kanbanTasks.tagFilterSupported} tags={projectTags} hasCurrentUser={Boolean(me?.user_id)}
              onAssigneePickerOpen={() => { void refreshTenantUsers().catch(() => {}); }} />}

            {!activeProject && (
              <Button type="button" size="sm" onClick={openProjectCreation}>
                <Plus />
                {t("projects:sectionProjectsNew")}
              </Button>
            )}
            {isMobile && <>
              <NotificationInbox />
              <SettingsMenu
                className="shrink-0"
                user={user}
                isAdminUser={isAdminUser}
                canManageTenantAgents={canManageTenantAgents}
                pushApi={pushApi}
                preferencesApi={preferencesApi}
                agents={agents}
                agentsLoading={agentsLoading}
                agentsError={agentsError}
              />
            </>}
          </header>

          {(projectLoadError || formError) && (
            <div className="mb-4 flex shrink-0 items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive sm:px-4">
              <AlertCircle className="size-4 shrink-0" />
              <span className="min-w-0 flex-1">{projectLoadError ?? formError}</span>
              {projectLoadError && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 shrink-0 px-2 text-xs"
                  onClick={() => { void refreshProjects(); }}
                >
                  {t("common:actions.retry")}
                </Button>
              )}
            </div>
          )}

          {!activeProject ? (
            <div className="flex min-h-[32rem] flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-muted-foreground">
              {isSelectedProjectUnavailable ? (
                <div
                  className="flex min-h-[32rem] flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground"
                  data-testid="project-unavailable"
                >
                  <Folder className="size-8" aria-hidden="true" />
                  <p className="text-sm">{t("projects:projectUnavailable")}</p>
                </div>
              ) : isSelectedProjectHydrating || projectsLoading ? (
                <>
                  <Loader2 className="size-6 animate-spin" />
                  <p className="text-sm">
                    {isSelectedProjectHydrating
                      ? t("projects:loadingProject")
                      : t("projects:loadingProjects")}
                  </p>
                </>
              ) : isTaskSelectionPending || hasMissingSelectedTask ? (
                <div
                  className="flex min-h-[32rem] flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground"
                  data-testid="project-task-route-state"
                >
                  {isTaskSelectionPending ? (
                    <>
                      <Loader2 className="size-5 animate-spin" />
                      <p className="text-sm">{t("projects:loadingTasks")}</p>
                    </>
                  ) : (
                    <p className="text-sm">
                      {kanbanTasks.taskLookupError
                        ? t("projects:taskLoadFailed")
                        : t("projects:noTasks")}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <Folder className="size-8" />
                  <p className="text-sm">{t("projects:noProjects")}</p>
                  <Button type="button" onClick={openProjectCreation}>
                    <Plus />
                    {t("projects:createProject")}
                  </Button>
                </>
              )}
            </div>
          ) : (
            <section className="flex min-h-[32rem] min-w-0 flex-1 flex-col">
              <KanbanSplitView
                activeTask={chatTask}
                kanban={kanbanBoard}
                onClose={() => openProject(activeProject.id)}
                chat={chatTask?.session_id ? renderTaskChat({
                    project: activeProject,
                    task: chatTask,
                    sessionId: chatTask.session_id,
                    initialMessage: null,
                    initialFiles: null,
                    onInitialMessageSent: () => {},
                    onClose: () => openProject(activeProject.id),
                    taskStatus: chatTask.status,
                    onTaskStatusChange: (status) => handleTaskStatusChange(chatTask.id, status),
                    onSaveDescription: (description) => handleUpdateTask(chatTask.id, { description }),
                    onSaveTitle: (title) => handleUpdateTask(chatTask.id, { title }),
                    onSaveDueDate: (dueDate) => handleUpdateTask(chatTask.id, { due_date: dueDate }),
                    onSaveAssignee: (assignedTo) => handleUpdateTask(chatTask.id, { assigned_to: assignedTo }),
                    onSaveTags: (tagIds) => handleUpdateTask(chatTask.id, { tags: tagIds }),
                    onArtifactRemoved: (updated) => kanbanTasks.upsert(updated),
                }) : null}
              />
            </section>
          )}
        </div>
      </div>

      <ProjectCreateDialog
        open={createDialogOpen}
        projectName={projectName}
        projectDirective={projectDirective}
        projectAgent={projectAgent}
        isPrivate={projectPrivate}
        agents={availableProjectAgents}
        agentsLoading={agentsLoading}
        agentsError={agentsError}
        creating={creatingProject}
        userRole={userRole}
        getToken={getToken}
        onOpenChange={(open) => {
          if (!open) {
            resetProjectForm();
            closeProjectCreation();
          }
        }}
        onNameChange={setProjectName}
        onDirectiveChange={setProjectDirective}
        onAgentChange={setProjectAgent}
        onShareSettingsChange={(settings) => {
          setProjectPrivate(settings.isPrivate);
          setProjectAllowedRoles(settings.allowedRoles);
        }}
        onCreate={handleCreateProject}
      />

      <ProjectEditDialog
        open={editDialogOpen}
        project={editProject}
        agents={availableProjectAgents}
        agentsLoading={agentsLoading}
        agentsError={agentsError}
        userRole={userRole}
        getToken={getToken}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) setEditProjectId(null);
        }}
        onSave={handleSaveProject}
      />
    </main>
  );
}
